// Admin Disputes — list view + resolve dialog.
//
// Sources from /v1/admin/disputes (filter view of tickets where
// ticket_kind='dispute'). Resolve via /v1/admin/disputes/:id/resolve
// with outcome ∈ {refund_full, refund_partial, no_action}. Resolving
// closes the linked engineer-rejected extra's dispute gate so the
// customer can confirm payment.

import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { GavelOutlined as ResolveIcon } from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { formatDateHK, formatHKD } from "@findgeeks/shared";
import { ApiError, apiFetch } from "@/lib/api";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { useToast } from "@/components/Toast";
import { usePagedQuery } from "@/hooks/usePagedQuery";

interface DisputeRow {
  id: string;
  subject: string;
  body: string | null;
  status: "open" | "in_progress" | "resolved" | "closed";
  related_job_id: string | null;
  opener_id: string;
  created_at: string;
  updated_at: string;
  opener: {
    id: string;
    display_name: string | null;
    email: string | null;
  } | null;
  job: {
    id: string;
    status: string;
    customer_id: string;
    engineer_id: string | null;
    base_price_cents: number | null;
    extras_total_cents: number | null;
    total_amount_cents: number | null;
  } | null;
}

type Outcome = "refund_full" | "refund_partial" | "no_action";

const STATUS_TABS: Array<{ id: string; label: string }> = [
  { id: "", label: "Open + In Progress" },
  { id: "resolved", label: "Resolved" },
  { id: "closed", label: "Closed" },
];

function statusChip(status: string): { color: string; bg: string; label: string } {
  switch (status) {
    case "open":
      return { color: "#B45309", bg: "#FEF3C7", label: "Open" };
    case "in_progress":
      return { color: "#1D4ED8", bg: "#DBEAFE", label: "In progress" };
    case "resolved":
      return { color: "#15803D", bg: "#DCFCE7", label: "Resolved" };
    case "closed":
      return { color: "#6B7280", bg: "#F3F4F6", label: "Closed" };
    default:
      return { color: "#6B7280", bg: "#F3F4F6", label: status };
  }
}

export function DisputeList() {
  const { show, ToastEl } = useToast();
  const [statusTab, setStatusTab] = useState<string>("");
  const [filters, setFilters] = useState<{ status?: string }>({});
  const [resolveOpen, setResolveOpen] = useState<DisputeRow | null>(null);

  const list = usePagedQuery<DisputeRow>("disputes", {
    filters,
    initialPagination: { page: 0, pageSize: 10 },
  });

  const onTabChange = (_e: React.SyntheticEvent, value: string): void => {
    setStatusTab(value);
    setFilters(value ? { status: value } : {});
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };

  const openResolve = (row: DisputeRow): void => {
    setResolveOpen(row);
  };

  const onResolved = (): void => {
    setResolveOpen(null);
    show("Dispute resolved", "success");
    list.refetch();
  };

  const columns: Array<DataTableColumn<DisputeRow>> = [
    {
      field: "subject",
      headerName: "Subject",
      render: (r) => (
        <Stack spacing={0.25}>
          <Typography variant="body2" fontWeight={600}>
            {r.subject}
          </Typography>
          {r.opener?.display_name ? (
            <Typography variant="caption" color="text.secondary">
              {`Opened by ${r.opener.display_name}${r.opener.email ? ` · ${r.opener.email}` : ""}`}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      field: "job_total",
      headerName: "Job total",
      width: 140,
      render: (r) => {
        if (!r.job) return <Typography variant="caption">—</Typography>;
        const total =
          r.job.total_amount_cents ??
          (r.job.base_price_cents ?? 0) + (r.job.extras_total_cents ?? 0);
        return (
          <Typography variant="body2" fontWeight={600}>
            {formatHKD(total / 100)}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      render: (r) => {
        const v = statusChip(r.status);
        return (
          <Chip
            label={v.label}
            size="small"
            sx={{
              color: v.color,
              backgroundColor: v.bg,
              fontWeight: 700,
              fontSize: 12,
            }}
          />
        );
      },
    },
    {
      field: "created_at",
      headerName: "Opened",
      width: 160,
      render: (r) => (
        <Typography variant="caption">{formatDateHK(r.created_at)}</Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      render: (r) =>
        r.status === "open" || r.status === "in_progress" ? (
          <Button
            size="small"
            variant="contained"
            startIcon={<ResolveIcon />}
            onClick={() => openResolve(r)}
            sx={{ textTransform: "none" }}
          >
            Resolve
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Disputes
      </Typography>

      <Paper sx={{ p: 0, mb: 2, border: `1px solid ${tokens.colors.admin.border}` }}>
        <Tabs
          value={statusTab}
          onChange={onTabChange}
          variant="standard"
          sx={{ px: 2 }}
        >
          {STATUS_TABS.map((t) => (
            <Tab key={t.id || "active"} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      <Paper sx={{ p: 2, border: `1px solid ${tokens.colors.admin.border}` }}>
        <DataTable<DisputeRow>
          rows={list.data?.items ?? []}
          rowCount={list.data?.total ?? 0}
          columns={columns}
          loading={list.isLoading || list.isFetching}
          paginationModel={list.paginationModel}
          onPaginationModelChange={list.setPaginationModel}
          getRowId={(r) => r.id}
        />
      </Paper>

      {resolveOpen ? (
        <ResolveDialog
          dispute={resolveOpen}
          onClose={() => setResolveOpen(null)}
          onResolved={onResolved}
        />
      ) : null}
      {ToastEl}
    </Box>
  );
}

function ResolveDialog({
  dispute,
  onClose,
  onResolved,
}: {
  dispute: DisputeRow;
  onClose: () => void;
  onResolved: () => void;
}): React.ReactElement {
  const [outcome, setOutcome] = useState<Outcome>("no_action");
  const [refundCents, setRefundCents] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: (payload: {
      outcome: Outcome;
      refund_cents?: number;
      resolution_notes: string;
    }) =>
      apiFetch(`/v1/admin/disputes/${dispute.id}/resolve`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => onResolved(),
    onError: (err) => {
      if (err instanceof ApiError) {
        const code = (err.body as { error?: string } | null)?.error ?? "";
        if (code === "dispute_already_resolved") {
          setError("This dispute was already resolved.");
          return;
        }
        if (code === "refund_cents_required") {
          setError("Enter a refund amount for partial / full refunds.");
          return;
        }
        setError(err.message || "Couldn't resolve. Try again.");
        return;
      }
      setError(err instanceof Error ? err.message : "Couldn't resolve.");
    },
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setError(null);
    const trimmedNotes = notes.trim();
    if (!trimmedNotes) {
      setError("Resolution notes are required.");
      return;
    }
    if (outcome === "no_action") {
      m.mutate({ outcome, resolution_notes: trimmedNotes });
      return;
    }
    const cents = Math.round(Number(refundCents) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setError("Enter a positive refund amount in HK$.");
      return;
    }
    m.mutate({ outcome, refund_cents: cents, resolution_notes: trimmedNotes });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resolve dispute</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Subject
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {dispute.subject}
            </Typography>
          </Box>
          {dispute.body ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Body
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", color: "#1E3A6E" }}
              >
                {dispute.body}
              </Typography>
            </Box>
          ) : null}
          <Box component="form" onSubmit={onSubmit}>
            <Stack spacing={2}>
              <TextField
                select
                label="Outcome"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value as Outcome)}
                fullWidth
                size="small"
              >
                <MenuItem value="no_action">
                  No action — close in engineer's favour (charge as billed)
                </MenuItem>
                <MenuItem value="refund_partial">
                  Partial refund — refund a portion to the customer
                </MenuItem>
                <MenuItem value="refund_full">
                  Full refund — release the entire job hold + cancel the job
                </MenuItem>
              </TextField>

              {outcome !== "no_action" ? (
                <TextField
                  label="Refund amount (HK$)"
                  value={refundCents}
                  onChange={(e) =>
                    setRefundCents(e.target.value.replace(/[^0-9.]/g, ""))
                  }
                  fullWidth
                  size="small"
                  helperText="Customer's wallet held + customer balance updates accordingly."
                />
              ) : null}

              <TextField
                label="Resolution notes (visible to engineer + customer)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                size="small"
                helperText="Required. Brief reasoning for the outcome."
              />

              {error ? (
                <Typography variant="caption" sx={{ color: "#EF4444" }}>
                  {error}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={(e) => onSubmit(e)}
          disabled={m.isPending}
          startIcon={<ResolveIcon />}
          sx={{ textTransform: "none" }}
        >
          {m.isPending ? "Resolving…" : "Resolve dispute"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
